import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Drawer,
  Form,
  Popconfirm,
  message,
  Space,
  Typography,
  InputNumber,
  Select,
  Tag,
  DatePicker,
} from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import adminApi from "./adminApi";

const { Title } = Typography;

export default function AttendeesPage() {
  const [attendees, setAttendees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sortOrder, setSortOrder] = useState("descend");
  const [dateRange, setDateRange] = useState(null);
  const [eventOptions, setEventOptions] = useState([]);
  const [form] = Form.useForm();

  const eventLabelFor = (key) => {
    const match = eventOptions.find((event) => event.value === key);
    return match ? match.label : key;
  };

  useEffect(() => {
    adminApi
      .get("/api/admin/events")
      .then(({ data }) => {
        setEventOptions(data.map((event) => ({ value: event.key, label: event.name })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/api/admin/attendees", {
        params: {
          search,
          page,
          limit: pageSize,
          sortOrder,
          startDate:
            dateRange && dateRange[0]
              ? dateRange[0].startOf("day").toISOString()
              : undefined,
          endDate:
            dateRange && dateRange[1]
              ? dateRange[1].endOf("day").toISOString()
              : undefined,
        },
      });
      setAttendees(data.attendees);
      setTotal(data.total);
    } catch (error) {
      message.error("Failed to load attendees.");
    }
    setLoading(false);
  }, [search, page, pageSize, sortOrder, dateRange]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      firstName: record.user && record.user.firstName,
      lastName: record.user && record.user.lastName,
      email: record.user && record.user.email,
      phone: record.user && record.user.phone,
      company: record.user && record.user.company,
      ticketAmount: record.ticketAmount,
      docNumber: record.docNumber,
      eventType: record.eventType,
    });
  };

  const closeEdit = () => {
    setEditing(null);
    form.resetFields();
  };

  const saveEdit = async () => {
    const values = await form.validateFields();
    const {
      firstName,
      lastName,
      email,
      phone,
      company,
      ticketAmount,
      docNumber,
      eventType,
    } = values;
    setSaving(true);
    try {
      await adminApi.patch(`/api/admin/attendees/${editing._id}`, {
        user: {
          ...editing.user,
          firstName,
          lastName,
          email,
          phone,
          company,
        },
        ticketAmount,
        docNumber,
        eventType,
      });
      message.success("Attendee updated.");
      closeEdit();
      load();
    } catch (error) {
      message.error("Failed to update attendee.");
    }
    setSaving(false);
  };

  const deleteAttendee = async (id) => {
    try {
      await adminApi.delete(`/api/admin/attendees/${id}`);
      message.success("Attendee deleted.");
      load();
    } catch (error) {
      message.error("Failed to delete attendee.");
    }
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) =>
        `${(record.user && record.user.firstName) || ""} ${
          (record.user && record.user.lastName) || ""
        }`.trim() || "—",
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => (record.user && record.user.email) || "—",
    },
    {
      title: "Company",
      key: "company",
      render: (_, record) => (record.user && record.user.company) || "—",
    },
    {
      title: "Event",
      dataIndex: "eventType",
      key: "eventType",
      render: (value) => <Tag>{value ? eventLabelFor(value) : "—"}</Tag>,
    },
    { title: "Tickets", dataIndex: "ticketAmount", key: "ticketAmount" },
    { title: "Invoice #", dataIndex: "docNumber", key: "docNumber" },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      sortOrder: sortOrder,
      render: (value) => (value ? new Date(value).toLocaleDateString() : "—"),
      filterIcon: () => (
        <CalendarOutlined style={{ color: dateRange ? "#1677ff" : undefined }} />
      ),
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                setPage(1);
                confirm();
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              onClick={() => {
                setDateRange(null);
                setPage(1);
                confirm();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Delete this attendee?"
            onConfirm={() => deleteAttendee(record._id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>Attendees</Title>
      <Input
        placeholder="Search by name, email, company, or invoice #"
        prefix={<SearchOutlined />}
        style={{ maxWidth: 400, marginBottom: "1rem" }}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        allowClear
      />
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={attendees}
        loading={loading}
        scroll={{ x: true }}
        pagination={{ current: page, pageSize, total }}
        onChange={(pagination, _filters, sorter) => {
          setPage(pagination.current);
          setPageSize(pagination.pageSize);
          setSortOrder(sorter.order || "descend");
        }}
      />
      <Drawer
        title="Edit Attendee"
        visible={!!editing}
        onClose={closeEdit}
        width={400}
        extra={
          <Button type="primary" onClick={saveEdit} loading={saving}>
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Event / Ticket Type" name="eventType">
            <Select options={eventOptions} />
          </Form.Item>
          <Form.Item label="First Name" name="firstName">
            <Input />
          </Form.Item>
          <Form.Item label="Last Name" name="lastName">
            <Input />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input />
          </Form.Item>
          <Form.Item label="Company" name="company">
            <Input />
          </Form.Item>
          <Form.Item label="Ticket Amount" name="ticketAmount">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item label="Invoice / Doc #" name="docNumber">
            <Input />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}
